// Public Terms of Service page. Lives outside the auth gate so Apple's
// App Review team, Google's OAuth consent screen, and the App Store
// listing's Terms URL can all reach it without signing in.
//
// This is the ONLY Terms page in the product — the Owner Hub (imageos.co)
// has no /terms route of its own — so it speaks to BOTH audiences: Operators
// (owner console, iOS app) and Pros (the Pro Hub at pro.imageos.co).
// The Pros sections (2–4) cover what pros actually do on the hub: profile
// content licensed into spotlights and printed QR signage, linking their own
// Google Business listing, the studio-run referral programme, and
// confidential studio access info. Access is tied to an active tenancy.
//
// The P1 set from the SaaS-terms benchmark (2026-08-28, Peter's go — see
// dev.cadenceadvisers.com/strategy/saas-terms-benchmark.html): publishing-
// grade license words (§3/§6 — networks require sublicensable rights from
// the poster), messaging compliance on the sender (§7), fees/auto-renewal
// (§8), feedback license (§10). Arbitration deliberately ABSENT — that one
// is with counsel, Peter's call, not a coordinator's.
//
// IMPORTANT: This is a starting draft. Suitable for App Store
// submission and OAuth consent screen, but Peter should have counsel
// review before relying on it for litigation purposes.
import { bareHost, OWNER_HUB_ORIGIN, PRO_HUB_ORIGIN } from '@/lib/hosts';
import { PRODUCT_NAME } from '@/lib/brand';

export default function Terms() {
  return (
    <div className="min-h-screen bg-brand-fog">
      <header className="px-6 pt-12 pb-6 text-center border-b border-brand-mist">
        <a href="/" className="inline-block">
          <h1 className="font-display text-3xl text-brand-deep">{PRODUCT_NAME}</h1>
        </a>
        <p className="text-sm text-brand-slate mt-1">Terms of Service</p>
      </header>

      <main className="px-6 py-10 max-w-3xl mx-auto">
        <article className="prose prose-sm sm:prose-base prose-headings:font-display prose-headings:text-brand-deep prose-p:text-brand-slate prose-li:text-brand-slate prose-a:text-image-pink hover:prose-a:text-image-pink-dark">

          <p className="text-xs uppercase tracking-[0.18em] text-brand-slate/70">
            Effective August 28, 2026
          </p>

          <p>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the Tenean iOS application
            and the Tenean web products at <strong>{bareHost(OWNER_HUB_ORIGIN)}</strong> (the &ldquo;Owner Hub&rdquo;)
            and <strong>{bareHost(PRO_HUB_ORIGIN)}</strong> (the &ldquo;Pro Hub&rdquo;) (together, the
            &ldquo;Service&rdquo;). The Service is operated by PKNT Enterprises
            (&ldquo;PKNT,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;).
          </p>

          <p>
            By signing in to or otherwise using the Service, you agree to these Terms. If you do not
            agree, do not use the Service.
          </p>

          <h2>1. Who May Use the Service</h2>
          <p>
            The Service supports two kinds of accounts:
          </p>
          <ul>
            <li>
              <strong>Operators</strong> — salon-suite location owners and their staff, using the
              Owner Hub and the iOS application to run their locations. Operator accounts are
              pre-staged by an authorized administrator; there is no public sign-up.
            </li>
            <li>
              <strong>Pros</strong> — independent salon professionals with an active suite tenancy or
              license at a participating studio, using the Pro Hub. Pro accounts are provided through
              your studio as part of your tenancy; there is no public sign-up.
            </li>
          </ul>
          <p>
            Whichever kind of account you hold, you may only use the Service with credentials assigned
            to you, and you agree to:
          </p>
          <ul>
            <li>Keep your sign-in credentials confidential.</li>
            <li>Not share your account with anyone else.</li>
            <li>Notify us immediately if you suspect unauthorized access.</li>
            <li>Be responsible for all activity that occurs under your account.</li>
          </ul>

          <h2>2. Pros: The Pro Hub and Your Studio</h2>
          <p>
            The Pro Hub is provided to you at no charge as a benefit of your tenancy at a
            participating studio. It is a tool, not an agreement:
          </p>
          <ul>
            <li>
              Nothing in the Service changes, replaces, or supplements your lease or license agreement
              with your studio. That agreement is between you and your studio; PKNT is not a party to
              it.
            </li>
            <li>
              You are an independent business. Nothing in the Service creates an employment,
              partnership, or agency relationship between you and PKNT or between you and your studio.
            </li>
            <li>
              Your access to the Pro Hub depends on your studio&rsquo;s participation in the Service
              and on your active tenancy. When either ends, your access may end with it.
            </li>
            <li>
              Information your studio maintains about your tenancy (such as your suite, your listed
              services, or your business name) originates with your studio. If something is wrong,
              you can correct much of it yourself in the Pro Hub; for the rest, contact your studio.
            </li>
          </ul>

          <h2>3. Pros: Your Profile, Photos, and Content</h2>
          <p>
            You can add content to the Service — for example a profile photo, your business name,
            a bio, the services you offer, and words or photos you contribute to a studio spotlight
            (&ldquo;Pro Content&rdquo;). You own your Pro Content. So that the features you use can
            work, you grant PKNT and your studio a non-exclusive, royalty-free, worldwide license to
            host, display, and reproduce your Pro Content — including the right to sublicense to
            the third-party platforms a feature publishes through, because a social network a
            spotlight is posted to needs its own copy of these rights to show the post:
          </p>
          <ul>
            <li>Within the Service (your profile, your studio&rsquo;s directory and roster).</li>
            <li>
              In your studio&rsquo;s marketing where a feature says so — for example a spotlight your
              studio publishes to its social channels, or printed materials such as a door sign
              bearing your QR code.
            </li>
          </ul>
          <p>
            You represent that you have the necessary rights to any content you add — including
            permission from any person who appears in a photo (such as a client) and rights to any
            work shown. You can update or remove your Pro Content at any time in the Pro Hub, but
            content that has already been printed, published, or distributed may persist in those
            forms.
          </p>
          <p>
            <strong>QR codes.</strong> Your studio may print signage carrying a QR code assigned to
            you. Where the code follows your profile, updating your profile updates where the code
            leads. Printed codes belong to the studio&rsquo;s signage program and remain associated
            with the location.
          </p>

          <h2>4. Pros: Reviews, Referrals, and Studio Access Information</h2>
          <ul>
            <li>
              <strong>Reviews.</strong> The Pro Hub can display your own public business listing and
              its reviews after you link it. You may only link a listing that is genuinely yours.
              Review content belongs to its authors and to the platform that hosts it; we display it
              and do not alter it.
            </li>
            <li>
              <strong>Referrals.</strong> Referral programmes shown in the Pro Hub are operated by
              your studio. Bonus amounts, eligibility, time windows, and payment are your
              studio&rsquo;s terms and your studio&rsquo;s obligation; the Service provides the
              tracking. A referral that expires under the programme&rsquo;s stated window earns no
              bonus.
            </li>
            <li>
              <strong>Access information.</strong> Wi-Fi passwords, door or access codes, and similar
              information shown in the Pro Hub are confidential, are for your own use only, and may be
              changed or revoked by your studio at any time. Do not share them.
            </li>
          </ul>

          <h2>5. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or in violation of any applicable regulation.</li>
            <li>Attempt to access data or features you have not been authorized to access.</li>
            <li>Reverse-engineer, decompile, or otherwise probe the Service for vulnerabilities except as expressly permitted by law.</li>
            <li>Upload content that is fraudulent, defamatory, or infringes another party's rights.</li>
            <li>Use the Service to impersonate any person or entity.</li>
            <li>Interfere with or disrupt the Service, our infrastructure, or other users' use.</li>
          </ul>

          <h2>6. Operators: Data Ownership</h2>
          <p>
            Operators retain all ownership rights to the data they enter into the Service, including
            tenant records, maintenance tickets, financial information, and operational notes
            (&ldquo;Operator Data&rdquo;). PKNT processes Operator Data solely on the operator's
            behalf to provide the Service — and as described in Section 9 — in accordance with the
            {' '}<a href="/privacy">Privacy Policy</a>.
          </p>
          <p>
            Operators are responsible for ensuring that they have the right to enter any Operator Data
            into the Service, including, where applicable, obtaining consent from the individuals
            whose information they record (such as tenants and prospects).
          </p>
          <p>
            When an operator uses the Service to publish content to a third-party platform (for
            example a social post), the operator grants us the worldwide, sublicensable rights
            needed to deliver that content through that platform on their behalf.
          </p>

          <h2>7. Messaging and Communications Compliance</h2>
          <p>
            The Service lets Operators send messages — including SMS and email — to their contacts.
            The Operator is the sender of every such message; PKNT provides the technology through
            which messages are composed and delivered and does not originate them. As the sender,
            you are solely responsible for the content of your messages and for compliance with
            every law that governs them — including the Telephone Consumer Protection Act (TCPA),
            the CAN-SPAM Act, and applicable state messaging laws. In particular, you agree to
            message only people who have given any legally required consent, to honor opt-out
            requests promptly, and to observe permitted calling and texting hours. We may suspend
            messaging features for an account we reasonably believe is using them unlawfully.
          </p>

          <h2>8. Fees and Payment</h2>
          <p>
            The Pro Hub is free for Pros. Operator use of the Service may require a paid
            subscription at the pricing presented when you purchase, or in a separate agreement
            with us. Unless that agreement says otherwise:
          </p>
          <ul>
            <li>
              Subscriptions renew automatically each billing period until cancelled. Cancelling
              stops future renewals; fees already accrued remain payable.
            </li>
            <li>
              Fees are billed in advance, in U.S. dollars, and are non-refundable except where the
              law requires otherwise.
            </li>
            <li>
              We may change pricing with reasonable prior notice; a change takes effect at your
              next renewal.
            </li>
            <li>You are responsible for applicable taxes, other than taxes on our income.</li>
            <li>
              We may suspend or terminate access for an account whose fees remain unpaid after
              notice.
            </li>
          </ul>

          <h2>9. Service Improvement and Aggregated Data</h2>
          <p>
            So that the Service can get better for everyone who uses it, we may use information about
            how the Service is used, and data derived from content and records in the Service, to
            operate, secure, maintain, and improve the Service and to develop new features — including
            improving the quality of the Service's AI-assisted features. Where we do this across
            studios or across users, we use the data in de-identified and/or aggregated form that does
            not identify you, your business, your studio, or your clients. We do not sell this data or
            share it with advertisers or data brokers. Individual records remain governed by Sections
            3 and 6 and the <a href="/privacy">Privacy Policy</a>.
          </p>

          <h2>10. Feedback</h2>
          <p>
            We welcome ideas — the Service even has a door built for them. If you submit ideas,
            suggestions, or feedback about the Service (through the Suggestion Box or otherwise),
            you grant PKNT a perpetual, irrevocable, non-exclusive, royalty-free license to use
            them for any purpose, without restriction and without any obligation to you. Feedback
            is voluntary and non-confidential, and we may already be working on something similar.
            A suggestion a Pro directs to their studio is also shared with that studio.
          </p>

          <h2>11. AI-Assisted Features</h2>
          <p>
            Some features generate content on request — for example a design, a caption, or a
            drafted reply. AI-generated output is a starting point, not a finished fact: you are
            responsible for reviewing it before you use, print, send, or publish it. Because these
            features respond to what they are asked, output generated for you may be similar to
            output generated for other users, and we make no promise that any output is accurate,
            original, or fit for a particular use. As between you and PKNT, you may use the output
            you generate through the Service in your business, subject to these Terms and any
            third-party rights in material the output incorporates.
          </p>

          <h2>12. Publicity</h2>
          <p>
            Unless you tell us in writing not to, we may identify an Operator&rsquo;s business by
            name and logo in our customer lists and marketing materials. This right ends when you
            ask us in writing to stop or when your use of the Service ends. We will not issue a
            press release about you without your approval. This section does not apply to Pros.
          </p>

          <h2>13. Service Availability and Modifications</h2>
          <p>
            We strive to keep the Service available 24/7 but cannot guarantee uninterrupted access.
            We may, at our sole discretion, modify, suspend, or discontinue any part of the Service at
            any time. We will provide reasonable notice of material changes when feasible.
          </p>

          <h2>14. Intellectual Property</h2>
          <p>
            The Tenean software, design, documentation, branding, trademarks, and all related
            intellectual property are owned by PKNT Enterprises or its licensors. These Terms do not
            grant you any ownership rights in the Service. Operators retain ownership of Operator Data
            as set forth in Section 6, and Pros retain ownership of Pro Content as set forth in
            Section 3.
          </p>

          <h2>15. Copyright Complaints (DMCA)</h2>
          <p>
            We respect the intellectual property of others and expect the same of everyone using
            the Service. If you believe content on the Service infringes your copyright, send a
            notice under the Digital Millennium Copyright Act to our copyright agent at the address
            in Section 24, including: identification of the work and of the allegedly infringing
            material, your contact information, a statement of good-faith belief that the use is
            unauthorized, and a statement under penalty of perjury that your notice is accurate and
            that you are the owner or authorized to act for the owner. We will remove or disable
            access to material identified in a valid notice, and we may terminate the accounts of
            repeat infringers.
          </p>

          <h2>16. Third-Party Services</h2>
          <p>
            The Service integrates with third-party providers (including Supabase, Anthropic, Google,
            and Apple, and — for participating studios — the studio's own CRM and messaging
            providers) to deliver functionality. Your use of those integrations is also subject to
            those providers' own terms of service. We are not responsible for the availability,
            accuracy, or actions of third-party services.
          </p>

          <h2>17. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE
            DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL
            COMPONENTS.
          </p>
          <p>
            The Service is a tool to help you run your business. It is not a substitute for
            professional legal, accounting, tax, or property-management advice. You are responsible for
            verifying the accuracy of data and for compliance with applicable law.
          </p>

          <h2>18. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, PKNT ENTERPRISES, ITS OFFICERS, EMPLOYEES, AGENTS,
            AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN
            ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            OUR TOTAL CUMULATIVE LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATED TO THE SERVICE WILL
            NOT EXCEED THE GREATER OF (a) THE AMOUNT YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS
            BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (b) ONE HUNDRED U.S. DOLLARS ($100).
          </p>

          <h2>19. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless PKNT Enterprises from any claim, demand, loss, or
            damages, including reasonable attorneys' fees, arising out of (a) your violation of these
            Terms, (b) your violation of any applicable law, or (c) content or data you add to the
            Service, including any claim that your Operator Data or Pro Content infringes a third
            party's rights.
          </p>

          <h2>20. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time, with or without cause
            and with or without notice, including (without limitation) if we believe you have violated
            these Terms. A Pro's access may also end when their tenancy at a participating studio
            ends or when their studio's participation in the Service ends. You may stop using the
            Service at any time by signing out. Upon termination, Sections 3, 6, 8 (as to fees
            already accrued), 9, 10, 14, 17, 18, 19, 23, and 24 survive.
          </p>

          <h2>21. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. The &ldquo;Effective&rdquo; date at the top of
            the page shows when the most recent version took effect. We will notify users of
            material changes through the Service or by email. Continued use of the Service after a
            change indicates your agreement to the updated Terms.
          </p>

          <h2>22. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the Commonwealth of Massachusetts, without regard
            to its conflict-of-laws principles. Any dispute arising out of or related to these Terms or
            the Service shall be brought exclusively in the state or federal courts located in Norfolk
            County, Massachusetts, and you consent to personal jurisdiction in those courts.
          </p>

          <h2>23. Miscellaneous</h2>
          <p>
            If any provision of these Terms is held invalid or unenforceable, the remaining provisions
            will remain in full effect. Our failure to enforce any right or provision is not a waiver
            of that right or provision. These Terms constitute the entire agreement between you and us
            concerning the Service — but, for Pros, they do not replace or modify your lease or
            license agreement with your studio.
          </p>

          <h2>24. Contact</h2>
          <p>For questions about these Terms:</p>
          <address className="not-italic">
            PKNT Enterprises — Legal / Copyright Agent<br />
            15 Cattail Lane<br />
            Sharon, MA 02067 USA<br />
            <a href="mailto:plewis@pkntindustries.com">plewis@pkntindustries.com</a>
          </address>

        </article>

        <div className="mt-12 pt-6 border-t border-brand-mist text-center">
          <a href="/" className="text-sm text-brand-slate hover:text-image-pink">
            ← Back to {PRODUCT_NAME}
          </a>
          <p className="mt-3 text-xs text-brand-slate/70">© 2026 Cadence Advisers</p>
        </div>
      </main>
    </div>
  );
}
